import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { BehavioralIntelligenceSegmentsResponse } from 'src/app/signals-insights/signals-insights.service';

/**
 * Dummy data for previewing the Shopify LIVE dashboard with results, without a
 * store that has any. Add `?dashboardMock=1` to the dashboard URL: the page
 * skips Learning Mode and the Nudge and Signals cards render the responses
 * below instead of calling the API. Nothing is sent or saved.
 */
export function isDashboardMock(): boolean {
  try {
    return (
      new URLSearchParams(window.location.search).get('dashboardMock') === '1'
    );
  } catch {
    return false;
  }
}

/** Resolves like a request would, so the skeletons show briefly. */
export function mockResponse<T>(value: T): Observable<T> {
  return of(value).pipe(delay(500));
}

/** `getPerformanceOverview`: the fields getWinningExperiments reads. */
export const MOCK_PERFORMANCE_OVERVIEW = {
  incentive: [
    '101.2041',
    '101.2017',
    '102.3005',
    '101.2033',
    '103.1102',
    '102.3011',
    '101.2050',
  ],
  incrementalRevenue: [18420.5, 12960.75, 9310.2, 6125.4, 3880.9, 1240.0, -860.3],
  rpv: { uplift: ['14.82', '11.35', '8.9', '6.41', '4.07', '1.6', '-2.3'] },
  cr: { uplift: ['9.6', '7.12', '5.48', '4.9', '2.15', '0.8', '-1.4'] },
  aov: { uplift: ['4.75', '3.91', '3.2', '1.42', '1.86', '0.75', '-0.9'] },
};

/** `listCampaigns`, with the per-nudge titles the single-campaign read adds. */
export const MOCK_CAMPAIGNS = [
  {
    campaignId: 'mock-campaign',
    name: 'Autumn conversion push',
    nudges: [
      ['101.2041', 'Low stock urgency on product page'],
      ['101.2017', 'Free shipping threshold progress bar in the cart drawer'],
      ['102.3005', 'Social proof: bought in the last 24 hours'],
      ['101.2033', 'Exit intent reminder with saved basket and a 10% first-order discount'],
      ['103.1102', 'Size guide hint'],
      ['102.3011', 'Bundle suggestion on checkout'],
    ].map(([sourceIncentiveID, name]) => ({
      sourceIncentiveID,
      name,
      pageType: 'product',
      status: 'ready' as const,
    })),
  },
];

/** `getBarChartCr`, for targets on the old nudge view. */
export const MOCK_BAR_CHART_CR = {
  incentives: 3.42,
  control: 2.87,
  p: 0.03,
  incentivesInfo: { visits: 48210, orders: 1649 },
  controlInfo: { visits: 5120, orders: 147 },
};

/** `getBarChartRpv`, for targets on the old nudge view. */
export const MOCK_BAR_CHART_RPV = {
  incentives: 4.18,
  control: 3.52,
  p: 0.06,
  incentivesInfo: { visits: 48210, revenue: 201518 },
  controlInfo: { visits: 5120, revenue: 18022 },
};

const meta = { value: 'meta', label: 'Meta', connected: true };
const klaviyo = { value: 'klaviyo', label: 'Klaviyo', connected: true };

/** `getBehavioralIntelligenceSegments`. */
export const MOCK_SEGMENTS: BehavioralIntelligenceSegmentsResponse[] = [
  {
    selectedStage: 'purchaseJourneyStages',
    label: 'Purchase Journey Stages',
    value: 'purchase_journey_stages',
    totalRevenue: 219540,
    items: [
      {
        label: 'Exploring',
        value: 'exploring',
        percentage: 34.6,
        sessions: 18452,
        totalRevenue: 21380,
        channels: [],
      },
      {
        label: 'Weighing Up',
        value: 'weighing_up',
        percentage: 22.1,
        sessions: 11786,
        totalRevenue: 48920,
        channels: [klaviyo],
      },
      {
        label: 'Just Looking',
        value: 'just_looking',
        percentage: 18.4,
        sessions: 9813,
        totalRevenue: 9260,
        channels: [],
      },
      {
        label: 'Ready To Buy',
        value: 'ready_to_buy',
        percentage: 15.2,
        sessions: 8106,
        totalRevenue: 121640,
        channels: [meta, klaviyo],
      },
      {
        label: 'Slipping Away',
        value: 'slipping_away',
        percentage: 9.7,
        sessions: 5173,
        totalRevenue: 18340,
        channels: [meta],
      },
    ],
  },
  {
    selectedStage: 'psychAffinities',
    label: 'Psychological Affinities',
    value: 'psych_affinities',
    totalRevenue: 219540,
    items: [
      { label: 'Social Proof Seeker', value: 'social_proof_seeker', percentage: 41.3, sessions: 22025, totalRevenue: 90420 },
      { label: 'Deal Hunter', value: 'deal_hunter', percentage: 33.9, sessions: 18078, totalRevenue: 71230 },
      { label: 'Quality Focused', value: 'quality_focused', percentage: 24.8, sessions: 13227, totalRevenue: 57890 },
    ],
  },
];
